import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnDestroy,
  Output
} from '@angular/core';
import { SzSuccessOverlayPhase } from './sz-success-overlay.types';

const BODY_LOCK_CLASS: string = 'sz-body-scroll-lock';

@Component({
  selector: 'sz-success-overlay',
  templateUrl: './sz-success-overlay.component.html',
  styleUrls: ['./sz-success-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class SzSuccessOverlayComponent implements OnDestroy {
  readonly open: InputSignal<boolean> = input<boolean>(false);
  readonly title: InputSignal<string> = input<string>('');
  readonly subtitle: InputSignal<string> = input<string>('');
  readonly autoDismissMs: InputSignal<number> = input<number>(1600);
  readonly phase: InputSignal<SzSuccessOverlayPhase> = input<SzSuccessOverlayPhase>('success');

  @Output() readonly dismiss: EventEmitter<void> = new EventEmitter<void>();

  private readonly document: Document = inject(DOCUMENT);
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect((): void => {
      const isOpen: boolean = this.open();
      const phase: SzSuccessOverlayPhase = this.phase();
      this.document.body.classList.toggle(BODY_LOCK_CLASS, isOpen);
      this.scheduleAutoDismiss(isOpen && phase === 'success');
    });
  }

  ngOnDestroy(): void {
    this.clearDismissTimer();
    this.document.body.classList.remove(BODY_LOCK_CLASS);
  }

  private scheduleAutoDismiss(enabled: boolean): void {
    this.clearDismissTimer();
    if (!enabled) return;
    const ms: number = this.autoDismissMs();
    if (ms <= 0) return;
    this.dismissTimer = setTimeout((): void => this.dismiss.emit(), ms);
  }

  private clearDismissTimer(): void {
    if (this.dismissTimer !== null) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }
}
